import cn from 'classnames';
import { forwardRef } from 'react';

import { IconXCircle } from 'components/UI/Icons/XCircle';
import { Error } from 'components/UI/Tailwind/Error';
import { Label } from 'components/UI/Tailwind/Label';
import { PrefixSuffixWrapper } from 'components/UI/Tailwind/PrefixSuffixWrapper';

type TButton = JSX.IntrinsicElements['button'];
type TInput = JSX.IntrinsicElements['input'];

export interface Input
  extends Omit<TInput, 'onReset' | 'prefix' | 'suffix'>, Label {
  error?: string;
  hint?: string;
  loading?: boolean;
  onReset?: VoidFunction;
  onSuffixClick?: TButton['onClick'];
  prefix?: JSX.Element | string;
  prefixClassName?: string;
  showErrorMessages?: boolean;
  suffix?: JSX.Element | string;
  buttonEnd?: JSX.Element | string;
  suffixClassName?: string;
  suffixTagName?: JSX.ElementType;
  variant?: 'task';
}

export const Input = forwardRef<HTMLInputElement, Input>(
  (
    {
      children,
      className,
      disabled,
      buttonEnd,
      error,
      hint,
      label,
      labelSuffix,
      loading,
      onReset,
      onSuffixClick,
      prefix,
      prefixClassName,
      required,
      showErrorMessages = true,
      suffix,
      suffixClassName,
      suffixTagName,
      value,
      variant,
      ...rest
    },
    ref,
  ) => (
    <div>
      {/* Linked to the input when it has an id, so the label names it. */}
      <Label
        htmlFor={rest.id}
        label={label}
        labelSuffix={labelSuffix}
        required={required}
      />
      <div
        className={cn(
          'group flex items-center border border-solid text-14/100% shadow-none outline-none transition duration-200 focus-within:border-blue-500 focus-within:outline-1 focus-within:outline-offset-0 focus-within:outline-blue-500',
          {
            'border-red-600 focus-within:border-red-600 focus-within:outline-red-100':
              error,
            'cursor-not-allowed bg-gray-200 text-gray-500': disabled,
            'cursor-text bg-white text-gray-800 focus-within:bg-red-50':
              !disabled && error,
            'cursor-text bg-white text-gray-800 focus-within:bg-blue-50':
              !disabled && !error,
            'h-8 rounded-[16px] border-gray-400': !variant,
            'pe-1': suffix,
            'ps-1': prefix || loading,
            'rounded-[4px] border-gray-300 hover:border-gray-400':
              variant === 'task',
          },
          className,
        )}
      >
        {(prefix || loading) && (
          <PrefixSuffixWrapper
            className={prefixClassName}
            disabled={disabled}
            item={prefix}
            loading={loading}
          />
        )}
        <input
          ref={ref}
          className={cn(
            'h-full w-full border-none bg-transparent px-3 py-2 text-gray-800 outline-none placeholder:text-gray-500',
            {
              'cursor-not-allowed': disabled,
              'pe-1': suffix,
              'ps-1': prefix || loading,
              'rounded-[16px] text-16/100% sm:text-14/100%': !variant,
              'text-heading-300 rounded-[4px]': variant === 'task',
            },
          )}
          disabled={disabled}
          type="text"
          value={value}
          {...rest}
        />
        {onReset && value && (
          <button
            className="pe-[7px] [&>svg]:h-[18px] [&>svg]:w-[18px] [&>svg]:text-gray-900"
            onClick={onReset}
            type="button"
          >
            <IconXCircle />
          </button>
        )}
        {suffix && (
          <PrefixSuffixWrapper
            className={suffixClassName}
            disabled={disabled}
            item={suffix}
            onClick={onSuffixClick}
            tagName={suffixTagName}
          />
        )}
        {buttonEnd}
      </div>
      {children}
      {hint && <span className="text-body-75 text-gray-500">{hint}</span>}
      {showErrorMessages && <Error className="pb-2">{error}</Error>}
    </div>
  ),
);
